import * as Popover from '@radix-ui/react-popover';
import { useAtlas } from '../app/store.ts';
import { scenarioById } from '../scenarios/index.ts';
import { doseFactor, drawnOverlays, drawnTissue, liveActive, mainSlot, methodsOf, slotsOf, stillView, type SceneMethods } from '../app/scene.ts';
import { brainLabel, brainText } from '../data/brain.ts';
import { brainMaps, onBrainCortex } from '../viewport/bridge.ts';
import { liveSolver, packHas } from '../app/live.ts';
import { clotsNow, ISLES_V5, islesView, partOf, reopensAt, siteOf, strokeChoices } from '../app/stroke.ts';
import { DENSITY, structureVessels } from '../data/sims/live.ts';
import { isCircuitKind } from '../data/sims/circuit.ts';
import { loadIsles } from '../data/sims/isles.ts';
import { defaultSource } from '../data/sims/files.ts';
import { TISSUE_TINT_METHODS } from '../data/sims/tissueByStructure.ts';
import { useLoaded } from '../ui/hooks.ts';
import { formatTime } from './time.ts';
import { PROVENANCE_LABEL, type ValidationRow } from '../data/sims/methods.ts';
import { formatValue } from '../ui/chart.ts';
import { isPatientComparison } from '../data/sims/comparison.ts';
import { formatCount } from '../data/measures.ts';
import { drawnCells } from '../app/framing.ts';
import { layersFor, MEMBRANE_MV } from '../data/sims/layers.ts';
import type { CellsHeader, CommonHeader } from '../data/sims/types.ts';
import { PatientMethods } from './PatientMethods.tsx';
import { DfcMethods, dfcActive } from './Dfc.tsx';
import { TextCortexMethods, TextMethods, textActive } from './Text.tsx';
import { CfdMethods } from './Cfd.tsx';
import { LifetimeMethods } from './Lifetime.tsx';
import { CENTILES_CAPTION, PD_METHODS, THICKNESS_METHODS, assignedVerdict, lifetimeActive, lifetimeCaption, modelLoaded, pdCaption, pdView, periodOf, thicknessCaption, thicknessView, useLifetime } from './lifetime.ts';
import { CFD_CAPTION, CFD_METHODS, cfdActive } from './cfd.ts';
import { JourneyMethods, journeyActive } from './Journey.tsx';
import { JOURNEY_METHODS, journeyCaption, useJourney } from './journey.ts';
import { TEXT_CAPTION, TEXT_METHODS } from './text.ts';
import { DFC_CAPTION, DFC_METHODS } from './dfc.ts';
import { useDrugDensity } from './drugDensity.ts';
import { MoleculeMethods } from './Molecule.tsx';
import { TARGET_LABEL } from './Traces.tsx';
import { previewWhy } from './preview.ts';

/** A citation or source as a link when it is one (a URL or a DOI), else as text. */
export function Ref({ text }: { text: string }) {
  const doi = /\b(10\.\d{4,9}\/\S+)/.exec(text)?.[1];
  const url = /^https?:\/\//.test(text) ? text : doi ? `https://doi.org/${doi.replace(/[).,;]+$/, '')}` : null;
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      {text}
    </a>
  ) : (
    <span>{text}</span>
  );
}

/** Model against reference, a row per comparison: the ratio is what to read. */
function ValidationTable({ rows, reference }: { rows: ValidationRow[]; reference?: string }) {
  const unit = rows.every((r) => r.unit === rows[0]!.unit) ? rows[0]!.unit : null;
  const within = rows.filter((r) => Math.abs(r.ratio - 1) <= 0.2).length;
  return (
    <div className="methods-validation">
      <div className="muted">
        {`${within} of ${rows.length} within ±20% of `}
        {reference ? <Ref text={reference} /> : 'the reference'}
      </div>
      <table className="methods-table num">
        <thead>
          <tr>
            <th />
            <th>{unit ? `Model, ${unit}` : 'Model'}</th>
            <th>Reference</th>
            <th>Ratio</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label}>
              <th scope="row">{r.label}</th>
              <td>{`${formatValue(r.model)}${unit ? '' : ` ${r.unit}`}`}</td>
              <td>{formatValue(r.reference)}</td>
              <td className={Math.abs(r.ratio - 1) > 0.2 ? 'off' : undefined}>{r.ratio.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One sim's part of the sheet. `display`: how its 3D map is made, where that isn't its own keys.
 *  `dose`: the Dose slider's course, where it isn't the run's own. */
function SimSection({ sim, display, dose, cortex }: { sim: SceneMethods['sims'][number]; display: string[]; dose?: string | null; cortex?: string | null }) {
  return (
    <section className="methods-sim">
      <h5 className="caps">{sim.slot === 'main' ? 'Simulation' : (sim.label ?? sim.slot)}</h5>
      {sim.slot !== 'main' && sim.text ? <p>{sim.text}</p> : null}
      <dl>
        {sim.model ? (
          <>
            <dt>Model</dt>
            <dd>
              {sim.model.name}
              {sim.model.version ? <span className="muted mono">{` ${sim.model.version}`}</span> : null}
              {sim.model.citation ? (
                <div className="muted">
                  <Ref text={sim.model.citation} />
                </div>
              ) : null}
              {sim.model.equations.length ? (
                <div className="methods-eq mono">
                  {sim.model.equations.slice(0, 8).map((e) => (
                    <div key={e}>{e}</div>
                  ))}
                </div>
              ) : null}
            </dd>
          </>
        ) : null}
        {sim.scope.length ? (
          <>
            <dt>Scope</dt>
            <dd>
              <ul className="methods-inputs">
                {sim.scope.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
        <dt>Output</dt>
        <dd>{sim.standIn ? `${sim.output} (stand-in)` : sim.output}</dd>
        {dose ? (
          <>
            <dt>Dose</dt>
            <dd className="methods-dose">{dose}</dd>
          </>
        ) : null}
        {cortex ? (
          <>
            <dt>Cortex</dt>
            <dd className="methods-cortex">{cortex}</dd>
          </>
        ) : null}
        {display.length ? (
          <>
            <dt>In 3D</dt>
            <dd>
              {display.map((line) => (
                <div key={line}>{line}</div>
              ))}
            </dd>
          </>
        ) : null}
        {sim.inputs.length ? (
          <>
            <dt>Inputs</dt>
            <dd>
              <ul className="methods-inputs">
                {sim.inputs.map((i, k) => (
                  <li key={k}>
                    <span>{i.what}</span>
                    <span className="muted">{` · ${i.provenance ? (PROVENANCE_LABEL[i.provenance] ?? i.provenance) : 'provenance not stated'}`}</span>
                    {i.source ? (
                      <span className="muted">
                        {' · '}
                        <Ref text={i.source} />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
        {sim.validation ? (
          <>
            <dt>Validation</dt>
            <dd>
              {sim.validation.summary ?? ''}
              {sim.validation.rows?.length ? <ValidationTable rows={sim.validation.rows} reference={sim.validation.reference} /> : sim.validation.reference ? <div className="muted"><Ref text={sim.validation.reference} /></div> : null}
              {sim.validation.report ? <div className="muted mono">{sim.validation.report}</div> : null}
            </dd>
          </>
        ) : null}
        <dt>Run</dt>
        <dd>
          <span className="mono">{sim.run.path}</span>
          {sim.run.space ? <span className="muted">{` · ${sim.run.space}`}</span> : null}
          {sim.run.created ? <span className="muted">{` · ${sim.run.created.slice(0, 16).replace('T', ' ')}`}</span> : null}
        </dd>
      </dl>
    </section>
  );
}

/** The ISLES'24 row: which scores, how many patients match the run on screen and how they were
 *  matched and scored, and the panel's medians: the model beside no infarct and the admission core. */
function IslesMethods() {
  const paths = useAtlas((s) => scenarioById(s.scenario).live?.isles);
  const exportScored = useAtlas((s) => scenarioById(s.scenario).live?.islesModel);
  const site = useAtlas((s) => strokeChoices(s).site ?? '');
  const part = useAtlas((s) => strokeChoices(s).part ?? '');
  const grade = useAtlas((s) => strokeChoices(s).grade ?? '');
  const treatment = useAtlas((s) => strokeChoices(s).treatment ?? '');
  const model = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.values?.model : undefined));
  const cmp = useLoaded(() => (paths?.length ? loadIsles(defaultSource(), paths) : null), [paths]);
  const view = islesView({ site, part, grade, treatment }, model, cmp.value ?? null, exportScored);
  if (!view) return null;
  const g = view.scores;
  // The evidence's own precision: volumes to 0.01 mL, Dice to 4 places.
  const ml = (v: number) => (Number.isFinite(v) ? String(Number(v.toFixed(2))) : '–');
  const dice = (v: number) => (Number.isFinite(v) ? String(Number(v.toFixed(4))) : '–');
  const numbers = `Median volume error ${ml(g.model.errMl)} mL, against ${ml(g.zero.errMl)} for no infarct and ${ml(g.core.errMl)} for the admission core; median Dice ${dice(g.model.dice)}, ${dice(g.zero.dice)} and ${dice(g.core.dice)}. The measured infarcts' median is ${ml(g.measuredMl)} mL.`;
  const hm = (iso: string) => iso.slice(11, 16);
  if (view.arm) {
    const src = ISLES_V5.source;
    const arm = view.arm === '3h' ? 'reperfused at 3 h' : 'untreated';
    return (
      <>
        <dt>ISLES’24</dt>
        <dd>
          {`v5's tissue model as this scene plays it (uniform allocation), against the ${g.n} ISLES’24 patients hemo assigned to this run: left M1 clots before the lenticulostriate arteries, ${arm} on its model clock (assignments, not their recorded treatment times), every collateral grade (v5's grades are one run), right-sided clots against the mirrored run. Each patient's follow-up DWI infarct, in the perfusion slab on their own CT grid, against three predictions: the model's, no infarct, and the admission CT-perfusion core. ${numbers} BLOOD-002 (ba-blood) registered its protocol at ${hm(src.protocolRegisteredUtc)} UTC and scored ${hm(src.scoredUtc[0])}–${hm(src.scoredUtc[1])} UTC on ${src.scoredUtc[0].slice(0, 10)}.`}
          <span className="muted mono">{` · ${src.result} · evidence ${src.bindingSha256.slice(0, 8)}`}</span>
        </dd>
      </>
    );
  }
  const against = model && view.scored !== model ? `Scored against ${view.scored}’s tissue runs, not the ${model} run on screen. ` : '';
  return (
    <>
      <dt>ISLES’24</dt>
      <dd>
        {`${against}${g.n} of the ${cmp.value?.cases.length ?? g.n} patients hemo matched to a tissue run match this one: the same clot site (for the M1, the same side of the lenticulostriate arteries), collateral grade and reperfusion arm, right-sided clots against the mirrored run. Each patient's follow-up DWI infarct (measured), on their own CT grid, against three predictions: the model's, no infarct, and the admission CT-perfusion core. ${numbers}`}
        <span className="muted mono">{` · ${view.from}`}</span>
      </dd>
    </>
  );
}

/** The live solver, while it drives the picture: what it is, what went in, and the clot. */
function LiveSection() {
  const on = useAtlas(liveActive);
  const stroke = useAtlas((s) => s.stroke);
  const clinical = useAtlas((s) => !!scenarioById(s.scenario).live?.clinical);
  const reopen = useAtlas(reopensAt);
  const reopened = useAtlas((s) => !!s.stroke.occlusion && clotsNow(s).length === 0);
  const catalogue = useAtlas((s) => s.catalogue);
  const solver = liveSolver();
  if (!on || !solver) return null;
  const name = (id: string) => solver.vessels.find((v) => v.id === id)?.name ?? id;
  const drawn = [...structureVessels(solver.vessels, packHas({ catalogue })).values()].flat().length;
  const o = stroke.occlusion;
  const what = clinical ? 'whenever the clot moves, the collaterals switch or a thrombectomy reopens the vessel' : 'whenever the clot moves';
  return (
    <section className="methods-sim methods-live">
      <h5 className="caps">Live flow</h5>
      <p>{`${solver.model}: the settled periodic flow and pressure on a ${solver.vessels.length}-vessel Circle of Willis network without subject geometry, re-solved ${what}. A clot is a sealed cut: no flow crosses it, and both stubs keep their wall compliance. Velocity is flow over each vessel's fixed reference area, and radius adds the model's linear wall stretch; both come from its own coefficients (density ${DENSITY} kg/m³). ${drawn} of the vessels colour the artery they name in this pack; the others have no geometry here and show in the trace.`}</p>
      <dl>
        <dt>Model</dt>
        <dd>{solver.model}</dd>
        <dt>Output</dt>
        <dd>
          {PROVENANCE_LABEL[solver.provenance] ?? solver.provenance}
          <span className="muted mono">{` · ${solver.space}`}</span>
        </dd>
        {solver.sources.length ? (
          <>
            <dt>Inputs</dt>
            <dd>
              <ul className="methods-inputs">
                {solver.sources.map((src) => (
                  <li key={src.id}>
                    <span className="mono">{src.id}</span>
                    {src.synthetic ? <span className="muted"> · synthetic</span> : null}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
        {solver.validation ? (
          <>
            <dt>Validation</dt>
            <dd>{`${solver.validation.cases} golden cases against independent references; largest flow error ${solver.validation.maxFlowError.toExponential(1)} mL/s`}</dd>
          </>
        ) : null}
        <dt>Clot</dt>
        <dd>
          {o
            ? `${name(o.vessel)}, ${Math.round(o.s * 100)} % along it${clinical && o.vessel === 'ba:left-mca-m1' ? (partOf(o) ? ', beyond the lenticulostriate arteries' : ', before the lenticulostriate arteries') : ''}${reopened && reopen !== null ? `; reopened at ${formatTime(reopen, 'h')}` : ''}`
            : 'None'}
        </dd>
        {clinical ? (
          <>
            <dt>Collaterals</dt>
            <dd>{stroke.collaterals ? 'ACom and both PComs open' : 'ACom and both PComs out of the network'}</dd>
            {siteOf(o?.vessel) === 'basilar' ? (
              <>
                <dt>PCom</dt>
                <dd>
                  {`${stroke.pcom === 'traced' ? 'Traced on the TOF-MRA: the left PCom 0.70 mm across, the right 0.667–0.70 mm' : 'As segmented from the TOF-MRA: the left PCom 0.3 mm across, the right absent'}. The basilar clot’s tissue run and its flows are that anatomy’s, and reperfusion restores its own baseline. The live solve keeps its own network, its two PComs alike.`}
                </dd>
              </>
            ) : null}
            {stroke.compare ? <IslesMethods /> : null}
          </>
        ) : null}
        <dt>Run</dt>
        <dd className="mono">{solver.path}</dd>
      </dl>
    </section>
  );
}

const count = (n: number) => n.toLocaleString('en-US');
const CLASS_NAME: Record<string, string> = { E: 'Excitatory', I: 'Inhibitory', untyped: 'Untyped' };

/** The activity the viewport plays on a tissue pack's neurons (H01's): the run, its model and input, the
 *  wiring it runs on, and how it differs from the same input with no wiring. */
function ActivitySection({ run }: { run: NonNullable<NonNullable<SceneMethods['tissue']>['run']> }) {
  const i = run.info;
  return (
    <section className="methods-sim methods-activity">
      <h5 className="caps">Activity</h5>
      {run.note ? <p>{run.note}</p> : null}
      <dl>
        <dt>Run</dt>
        <dd>
          {run.title}
          {i?.label ? <span className="muted">{` · ${i.label}`}</span> : null}
        </dd>
        {i ? (
          <>
            <dt>Model</dt>
            <dd>
              {i.model.name}
              {i.model.basis ? <div className="muted">{i.model.basis}</div> : null}
            </dd>
            {i.input ? (
              <>
                <dt>Input</dt>
                <dd>
                  {i.input.description}
                  <div className="muted">{`${count(i.input.neurons)}${i.input.modelled !== undefined ? ` of ${count(i.input.modelled)} modelled` : ''} neurons driven`}</div>
                </dd>
              </>
            ) : null}
            {i.wiring ? (
              <>
                <dt>Wiring</dt>
                <dd>
                  {`${count(i.wiring.kept)} of the pack’s ${count(i.wiring.of)} connections${i.wiring.synapses !== undefined ? ` (${count(i.wiring.synapses)} synapses)` : ''}, from a typed neuron onto a neuron`}
                  {i.wiring.selfEdges ? <div className="muted">{i.wiring.selfEdges}</div> : null}
                </dd>
              </>
            ) : null}
            {i.control ? (
              <>
                <dt>Without wiring</dt>
                <dd>{`The same input with no connections: ${count(i.control.same)} neurons spike identically, ${count(i.control.changed)} change (${count(i.control.added)} spikes added, ${count(i.control.removed)} removed)`}</dd>
              </>
            ) : null}
            {i.classes ? (
              <>
                <dt>Rates</dt>
                <dd>{i.classes.map((c) => `${CLASS_NAME[c.name] ?? c.name} ${c.active ? `${formatValue(c.rateHz)} Hz` : 'silent'} (${count(c.neurons)})`).join(' · ')}</dd>
              </>
            ) : null}
            <dt>Played</dt>
            <dd>{[formatTime(i.durationS, 's'), i.frameMs !== undefined ? `${i.frameMs} ms frames` : null, i.spikes !== undefined ? `${count(i.spikes)} spikes` : null, i.seed !== undefined ? `seed ${i.seed}` : null].filter(Boolean).join(' · ')}</dd>
            {i.model.implementation ? (
              <>
                <dt>Code</dt>
                <dd className="mono">{`${i.model.implementation}${i.model.commit ? ` @ ${i.model.commit.slice(0, 8)}` : ''}`}</dd>
              </>
            ) : null}
          </>
        ) : null}
      </dl>
    </section>
  );
}

/** A tissue pack in its own view: where the data comes from, and how each drawn layer was made. */
function TissueSection({ tissue }: { tissue: NonNullable<SceneMethods['tissue']> }) {
  return (
    <section className="methods-sim">
      <h5 className="caps">Data</h5>
      <dl>
        {tissue.citation ? (
          <>
            <dt>Source</dt>
            <dd>
              <Ref text={tissue.citation} />
            </dd>
          </>
        ) : null}
        {tissue.data.length ? (
          <>
            <dt>Layers</dt>
            <dd>
              <ul className="methods-inputs">
                {tissue.data.map((d) => (
                  <li key={d.what}>
                    <span>{d.what}</span>
                    <span className="muted">{` · ${PROVENANCE_LABEL[d.provenance] ?? d.provenance}`}</span>
                    {d.method ? <div className="muted">{d.method}</div> : null}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
        {tissue.placement ? (
          <>
            <dt>Placed in</dt>
            <dd>
              <span className="mono">{tissue.placement.space}</span>
              {tissue.placement.provenance ? <span className="muted">{` · ${PROVENANCE_LABEL[tissue.placement.provenance] ?? tissue.placement.provenance}`}</span> : null}
              {tissue.placement.method ? <div className="muted">{tissue.placement.method}</div> : null}
            </dd>
          </>
        ) : null}
        {tissue.licence ? (
          <>
            <dt>Licence</dt>
            <dd>{tissue.licence.url ? <Ref text={tissue.licence.url} /> : tissue.licence.name}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

/** The Methods caption under the picture, and the sheet it opens (also from the bar's Methods button). */
export function Methods() {
  const scenario = useAtlas((s) => scenarioById(s.scenario));
  const methods = useAtlas(methodsOf);
  const drawn = useAtlas(drawnOverlays);
  // Stroke's tissue state per structure, while it's on the picture (data/sims/tissueByStructure.ts).
  const tinted = useAtlas((s) => drawnTissue(s).length > 0);
  const live = useAtlas(liveActive);
  // A short line for the pill; the sheet's Live flow section names the model in full.
  const tissue = useAtlas((s) => !!scenarioById(s.scenario).live?.clinical && !!siteOf(s.stroke.occlusion?.vessel));
  // A still view (ba-web's Dfc.tsx, Text.tsx) draws its own comparison: its caption and Methods section stand in for the scene's.
  const still = useAtlas(stillView);
  const dfc = useAtlas(dfcActive);
  const text = useAtlas(textActive);
  // An aside view (ba-web's Cfd.tsx, Blood flow's 3D flow) draws its own run: its caption and section stand in for the scene's runs'.
  const cfd = useAtlas(cfdActive);
  // Lifetime (ba-web's Lifetime.tsx): the sequence's frames, surfaces and maps, under the scene's sim section.
  const lifetime = useAtlas(lifetimeActive);
  const lifetimeModel = useAtlas(modelLoaded);
  const lifetimePeriod = useAtlas(periodOf);
  // The assigned-thickness track (LIFE-020) names itself and its whole-set verdict in the caption while it leads.
  const lifetimeAssigned = useAtlas(assignedVerdict);
  const lifetimeTrack = useLifetime((s) => s.track);
  const pd = useAtlas(pdView);
  const th = useAtlas(thicknessView);
  const thMode = useLifetime((s) => s.thMode);
  const centilesOn = useLifetime((s) => s.chart === 'centiles');
  // A journey view (ba-web's Journey.tsx): the stage on screen and its data in the caption, the dive's section in the sheet.
  const journey = useAtlas(journeyActive);
  const journeyName = useJourney((s) => s.name);
  const journeyReply = useJourney((s) => s.reply);
  const journeyStage = useJourney((s) => s.stage);
  const caption = journey ? journeyCaption(journeyName, journeyReply, journeyStage) : lifetime ? (th ? thicknessCaption(thMode) : pd ? pdCaption() : lifetimeCaption(lifetimeModel, lifetimePeriod, lifetimeTrack === 'assigned' ? lifetimeAssigned : null) + (centilesOn ? CENTILES_CAPTION : '')) : cfd ? CFD_CAPTION : dfc ? DFC_CAPTION : text ? TEXT_CAPTION : live ? (tissue ? 'Live 1D flow + IXI-165 tissue' : 'Live 1D flow · Circle of Willis') : methods.caption;
  const simsStatus = useAtlas((s) => (s.sims.scenario === s.scenario ? s.sims.status : 'loading'));
  const catalogue = useAtlas((s) => s.catalogue);
  const engine = useAtlas((s) => s.engine);
  const patient = useAtlas((s) => {
    const h = mainSlot(s)?.header;
    return !!h && isPatientComparison(h);
  });
  const viewPack = useAtlas((s) => s.viewPack);
  const open = useAtlas((s) => s.methodsOpen);
  const setOpen = useAtlas((s) => s.setMethods);
  const brain = useAtlas((s) => (s.engine.kind === 'wgpu' ? s.brain : null));
  const loadMaps = useAtlas((s) => s.load.maps);
  const slots = useAtlas(slotsOf);
  const drawnCellsPath = useAtlas((s) => drawnCells(s)?.path ?? null);
  const factor = useAtlas(doseFactor);
  const drug = useAtlas((s) => s.drug);
  // Drugs' Show control (features/drugDensity.ts): the receptor's density alone on the cortex, in the engagement's place.
  const drugShow = useDrugDensity((s) => s.show);
  const drugMap = useDrugDensity((s) => s.map);
  const drugMapLine =
    drugShow !== 'engagement' && drugMap
      ? drugMap.show === 'pet'
        ? `${TARGET_LABEL(drugMap.target)}'s density alone, not occupancy × density: the [11C]Cimbi-36 group PET map (Beliveau 2017, 29 adults; compiled by Hansen 2022), ${drugMap.units}, vertex by vertex on the cortex drawn, moved onto its mesh from pharma's fs_LR 32k export atlas-vertex-v1 (${drugMap.pack}, layer ${drugMap.layer}; ${drugMap.finite.toLocaleString('en-GB')} of ${drugMap.vertices.toLocaleString('en-GB')} vertices have a value).`
        : `${TARGET_LABEL(drugMap.target)}'s density alone, not occupancy × density: Hansen 2022's author-published PET parcel means on Schaefer-400, min–max scaled within the target (relative 0–1), read through the drawn cortex's schaefer400 labels (${drugMap.pack}, table ${drugMap.layer}).`
      : null;
  const unit = drug?.unit ? ` ${drug.unit}` : '';
  const dose =
    drug && factor !== 1
      ? `${formatValue(drug.dose * factor)}${unit}, from the course's ${formatValue(drug.dose)}${unit} on its own model. Its PK is linear in the dose, so every concentration is scaled by ${Number(factor.toPrecision(3))}; each target's occupancy is C / (C + Ki) across its Ki range; and the cortex's engagement, occupancy × density, scales by the occupancy ratio. The run's own series follow that model.`
      : null;
  const pack = catalogue?.pack;
  const maps = brainMaps(loadMaps, brain?.maps, engine.sims, engine.maps);
  // A cells run the viewport draws: the voltage its arbors glow over. A circuit drawn through its close-up:
  // what of it the viewport draws, said in both runs' sections.
  const cellsLines = (slot: string): string[] => {
    const x = slots.find((y) => y.slot === slot);
    if (x?.format !== 'cells') return [];
    const cells = (h?: CommonHeader) => (h as CellsHeader | undefined)?.counts?.cells;
    const out: string[] = [];
    if (x.drawnFor) {
      const n = cells(x.header);
      const of = (x.header as CellsHeader).recording?.simulated_cells;
      if (n && of) out.push(`${formatCount(n)} of the ${formatCount(of)} cells its simulation ran, with every compartment recorded`);
    }
    const by = slots.find((y) => y.drawnFor === slot && !y.appOnly);
    const n = cells(by?.header);
    const of = cells(x.header);
    if (by && n && of) out.push(`${formatCount(n)} of its ${formatCount(of)} cells, through the close-up below`);
    const range = x.path === drawnCellsPath ? layersFor(x.slot, 'cells', x.header).find((l) => l.name === 'arbors')?.range : undefined;
    const mv = (v: number) => `${v < 0 ? '−' : ''}${Math.abs(v)}`;
    if (range) out.push(`Membrane voltage on the activity ramp from ${mv(range[0])} to ${mv(range[1])} mV${isCircuitKind(x.header.kind) ? `, narrower than a single cell’s ${mv(MEMBRANE_MV[0])} to ${mv(MEMBRANE_MV[1])} mV so a spike shows at the column’s scale` : ''}`);
    return out;
  };
  // A run the viewport plays on the shown brain through a map: Methods says it was moved there.
  const onBrain = (slot: string, space?: string) => {
    const x = slots.find((y) => y.slot === slot && !y.appOnly);
    return brain && x && onBrainCortex(x.header, pack?.space, maps) ? [`On ${brainLabel(brain)}, through its map from ${space ?? x.header.space}`] : [];
  };
  // A run drawn on the shown brain through a map sends no overlays, so their methods (DKT regions) aren't what's on screen.
  const displayLines = (slot: string, space?: string) => {
    const on = onBrain(slot, space);
    return [...on, ...cellsLines(slot), ...(on.length ? [] : drawn.filter((o) => o.method && o.id.startsWith(`${slot}:`)).map((o) => o.method!)), ...(slot === 'main' && tinted ? [TISSUE_TINT_METHODS] : [])];
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="methods" data-inset="bottom-left" aria-label={`Methods: ${caption}`}>
          <span className="methods-overline ba-type-overline">Methods</span>
          <span className="methods-text ba-type-small">{caption}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        {/* Radix closes the popover on Escape at the document, before the app's shortcuts see the key on the
            window: stopped here, that Escape closes Methods and doesn't also clear the selection. */}
        <Popover.Content className="popover methods-pop" side="top" align="start" sideOffset={12} collisionPadding={24} onEscapeKeyDown={(e) => e.stopPropagation()}>
          <h4>Methods</h4>
          <p>{journey ? JOURNEY_METHODS : cfd ? CFD_METHODS : dfc ? DFC_METHODS : text ? TEXT_METHODS : th ? THICKNESS_METHODS : pd ? PD_METHODS : methods.text}</p>
          <LiveSection />
          {patient ? <PatientMethods /> : null}
          {journey ? (
            <JourneyMethods />
          ) : cfd ? (
            <CfdMethods />
          ) : dfc ? (
            <DfcMethods />
          ) : text ? (
            <>
              <TextMethods />
              <TextCortexMethods />
            </>
          ) : still || lifetime ? null : (
            methods.sims.map((sim) => <SimSection key={sim.slot} sim={sim} dose={sim.slot === 'main' ? dose : null} cortex={sim.slot === 'main' ? drugMapLine : null} display={displayLines(sim.slot, sim.run.space)} />)
          )}
          {lifetime ? <LifetimeMethods /> : null}
          {scenario.id === 'drugs' ? <MoleculeMethods /> : null}
          {methods.tissue?.run ? <ActivitySection run={methods.tissue.run} /> : null}
          {methods.tissue ? <TissueSection tissue={methods.tissue} /> : null}
          <dl>
            {pack ? (
              <>
                <dt>{methods.tissue ? 'Tissue' : 'Anatomy'}</dt>
                <dd>
                  <span className="mono">{pack.id}</span> <span className="muted mono">{`v${pack.version}`}</span>
                  {pack.extends?.length ? <span className="muted">{` over ${pack.extends.map((r) => `${r.id}${r.version ? ` v${r.version}` : ''}`).join(', ')}`}</span> : null}
                </dd>
                <dt>Space</dt>
                <dd className="mono">{pack.space}</dd>
              </>
            ) : null}
            {brain ? (
              <>
                <dt>Cortex</dt>
                <dd>
                  {brainText(brain)} <span className="mono">{brain.pack}</span> {brain.version ? <span className="muted mono">{`v${brain.version}`}</span> : null}
                </dd>
              </>
            ) : null}
            {!methods.sims.length && !methods.tissue?.run ? (
              <>
                <dt>Simulation</dt>
                <dd>
                  {viewPack ? (
                    <span className="muted">None in this view</span>
                  ) : scenario.sims?.length ? (
                    <span className="muted">{simsStatus === 'loading' ? 'Looking for its run…' : 'Not built yet'}</span>
                  ) : (
                    <span className="muted">None in this scene</span>
                  )}
                </dd>
              </>
            ) : null}
            <dt>Renderer</dt>
            <dd>{engine.kind === 'wgpu' ? <span>wgpu <span className="mono">{engine.version?.slice(0, 7) ?? ''}</span> · {engine.adapter ?? ''}</span> : engine.kind === 'mock' ? (engine.fallback ? `2D preview (${previewWhy(engine.fallback)})` : new URLSearchParams(location.search).get('engine') === 'mock' ? 'Mock viewport (?engine=mock)' : 'Mock viewport') : 'Starting…'}</dd>
          </dl>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
